<template>
  <b-row class="wrapper" align-v="stretch">
    <b-col class="c-dropdown" cols="12" v-if="conversations.length > 0">
      <b-dropdown right>
        <template slot="button-content">
          {{ peerName(activePeer) }}
        </template>
        <b-dropdown-item v-for="c in conversations" :key="c.peer.id" @click="activateConversation(c)">
          {{ peerName(c.peer) }}
        </b-dropdown-item>
      </b-dropdown>
    </b-col>
    <b-col class="c-wrapper" cols="4" v-if="conversations.length > 0">
      <div class="scrollable">
        <div :class="conversationClass(c)" v-for="c in conversations" :key="c.peer.id" @click="activateConversation(c)">
          <img :src="peerAvatar(c.peer)">
          <div class="info">
            <router-link class="name" :to="peerLink(c.peer)">{{ peerName(c.peer) }}</router-link>
            <div class="time">
              <span v-if="c.unread_count" class="unread-count">{{ c.unread_count}} unread,</span>
              {{ formatDate(c.last_message.created_at) }}
            </div>
          </div>
          <div class="actions">
            <Report :artist="c.peer.artist"
                    :profile="c.peer.profile"
                    label="Report conversation"
                    class="report-link">
                <LinkButton @click="blockConversation(c)">Block user</LinkButton>
            </Report>
          </div>
        </div>
      </div>
    </b-col>

    <b-col class="m-wrapper" v-if="conversations.length > 0">
      <div class="messages scrollable" ref="messages">
        <div :class="messageClass(msg)" v-for="msg in messages" :key="msg.id">
          <img :src="peerAvatar(activePeer, msg.direction)">
          <div class="content">
            <p v-for="(p, idx) in msgLines(msg)" :key="idx" v-html="linkifyHandles(p)">
            </p>
            <div class="time">
              {{ formatDate(msg.created_at) }}
            </div>
          </div>
        </div>
      </div>

      <div class="post" v-if="activePeer">
        <b-form-textarea ref="input" :rows="2" :max-rows="5" required v-model="text"></b-form-textarea>
        <b-button size="sm" class="send" @click="send">Send</b-button>
      </div>
    </b-col>

    <b-col class="empty" v-if="!conversations.length">
      No messages
    </b-col>
  </b-row>
</template>

<style scoped lang="scss">
  @import '@/assets/custom.scss';
  @import '~bootstrap/scss/mixins/_breakpoints.scss';

  .wrapper {
    flex-grow: 1;
    margin-left: -15px;
    flex-direction: column
  }

  .scrollable {
    overflow-y: scroll;
    height: 0px;
    flex: 1 1 auto;

    &::-webkit-scrollbar {
      width: 10px;
      background-color: #49495b;
    }

    &::-webkit-scrollbar-track {
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 5px;
      background-color: $steel;
      border: 2px solid #49495b;
    }
  }

  .c-dropdown {
    width: 100%;

    /deep/ .dropdown {
      width: 100%;

      button {
        width: 100%;
      }
    }
  }

  .c-wrapper {
    background: $dark;
    padding: 0;
    display: none;
    flex-direction: column;

    .conversation {
      padding: 1em 0.5em 1em 1em;
      display: flex;
      align-items: start;
      border-bottom: 1px solid $body-bg;

      &.unread {
        border-right: 2px solid $darkish-pink;

        .unread-count {
          color: $white;
        }
      }

      &.active {
        background-image: linear-gradient(to right, $darkish-pink, rgba(128, 51, 211, 0.4) 60%, rgba(128, 51, 211, 0.0) 90%);

        .name {
          text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);
        }

        .info .time {
          color: $white;
        }
      }

      img {
        display: none;
        width: 48px;
        height: 48px;
        border-radius: 100%;
        margin-right: 10px;
        box-shadow: 4px 4px 6px 0 rgba(0, 0, 0, 0.5);
      }

      @include media-breakpoint-up(lg) {
        img {
          display: block;
        }
      }

      .info {
        align-self: stretch;
        flex-grow: 1;

        .name {
          font-weight: bold;
          color: $white;
          text-decoration: none;

          &:hover {
            text-decoration: underline;
          }
        }

        .time {
          font-size: 0.8em;
          color: $steel;
        }
      }

      .report-link {
        /deep/ svg {
          color: $steel;

          &:hover {
            color: white;
          }
        }
      }

    }
  }

  @include media-breakpoint-up(sm) {
    .wrapper {
      flex-direction: row;
    }

    .c-dropdown {
      display: none;
    }

    .c-wrapper {
      display: flex;
    }
  }

  .m-wrapper {
    display: flex;
    flex-direction: column;
    margin-right: -15px;

    .message {
      padding: 1em 15px 1em 0;

      display: flex;
      align-items: top;

      img {
        display: none;
        width: 48px;
        height: 48px;
        border-radius: 100%;
        box-shadow: 4px 4px 6px 0 rgba(0, 0, 0, 0.5);
      }

      @include media-breakpoint-up(xl) {
        img {
          display: block;
        }
      }

      .content {
        padding: 0.5em 0.5em 0 0.5em;
        flex: 1;
        position: relative;
        text-align: left;

        border-radius: 3px;
        background-color: #c53073;
        box-shadow: 4px 2px 10px 0 rgba(0, 0, 0, 0.56);

        p:last-of-type {
          margin-bottom: 0.25em;
        }

        .time {
          text-align: right;
          font-size: 0.8em;
          color: $body-bg;
        }

        /deep/ a {
          color: $white;

          &:hover {
            text-decoration: underline;
          }
        }
      }

      &.received {
        img {
          margin-right: 16px;
        }

        .content {
          background-color: #c53073;

          &:after {
            right: 100%;
            top: 14px;
            border: solid transparent;
            content: " ";
            height: 0;
            width: 0;
            position: absolute;
            pointer-events: none;
            border-right-color: #c53073;
            border-width: 8px;
          }
        }
      }

      &.sent {
        flex-direction: row-reverse;

        img {
          margin-left: 16px;
        }

        .content {
          background-color: $slate-blue;

          &:after {
            left: 100%;
            top: 14px;
            border: solid transparent;
            content: " ";
            height: 0;
            width: 0;
            position: absolute;
            pointer-events: none;
            border-left-color: $slate-blue;
            border-width: 8px;
          }
        }
      }
    }

    .post {
      display: flex;
      align-items: start;
      padding: 0.5em 0 0.5em 13px;
      background: $dark;
      margin-left: -13px;
      border-radius: 4px;

      input {
        display: inline-block;
        flex: 1;
      }

      .send {
        margin-left: 1em;
        margin-right: 1em;
      }
    }

  }

  .empty {
    align-self: center;
    text-align: center;
    color: $steel;
  }
</style>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import moment from '@/utils/moment'
import sortBy from 'lodash/sortBy'

import FontAwesomeIcon from '@fortawesome/vue-fontawesome'

import FanProfileTabs from '@/components/widgets/FanProfileTabs'
import Report from '@/components/widgets/Report'
import LinkButton from '@/components/widgets/LinkButton'

import { profileHelpers } from '@/store/helpers'
import { linkifyHandles } from '@/utils/textformat'

export default {
  name: 'Messages',
  components: {
    FanProfileTabs,
    Report,
    FontAwesomeIcon,
    LinkButton
  },
  props: {
    inbox: Object
  },
  data () {
    return {
      conversations: [],
      activePeer: null,
      messages: [],
      text: ''
    }
  },
  computed: {
    ...mapGetters('realtime', ['latestMessage']),
    ...mapGetters('profile', ['currentUser'])
  },
  created () {
    this.initComponent()
  },
  watch: {
    'inbox': 'initComponent',
    'latestMessage': 'checkLatestMessage',
    'messages': 'doScroll'
  },
  methods: {
    async initComponent () {
      const response = await this.$store.dispatch('profile/listConversations', {inboxId: this.inbox.id})
      this.conversations = sortBy(await response.all(), c => moment().diff(c.last_message.created_at))
      if (this.conversations.length) {
        this.activateConversation(this.conversations[0])
      } else {
        this.activePeer = null
        this.messages = []
      }
    },
    async loadMessages () {
      this.messages = []
      if (!this.inbox || !this.activePeer) return

      const response = await this.$store.dispatch('profile/listMessages', {
        inboxId: this.inbox.id,
        peerId: this.activePeer.id
      })
      this.messages = await response.all()
      this.messages.reverse()
    },
    async markAsRead () {
      const newUnread = await this.$store.dispatch('profile/markAsRead', {
        inboxId: this.inbox.id,
        peerId: this.activePeer.id
      })

      this.inbox.unread_count = newUnread
    },
    checkLatestMessage () {
      const msg = this.latestMessage
      if (msg.inbox.id !== this.inbox.id) return

      // if this conversation is currently active, show the latest message and mark it as read immediately
      if (this.activePeer && this.activePeer.id === msg.peer) {
        this.messages.push(this.latestMessage)
        this.markAsRead()

        for (let c of this.conversations) {
          if (c.peer.id === this.activePeer.id) {
            c.unread_count = 0
          }
        }
      }
    },
    peerAvatar (peer, direction) {
      if (direction === 'sent') {
        if (this.inbox.is_artist_inbox) return this.currentUser.artist.avatar_image
        return this.avatarImage(this.currentUser)
      }
      if (peer.artist) return peer.artist.avatar_image
      if (peer.profile) return this.avatarImage(peer.profile)
      return null
    },
    peerName (peer) {
      if (!peer) return ''
      if (peer.artist) return peer.artist.name
      if (peer.profile) return this.displayName(peer.profile)
      return ''
    },
    peerLink (peer) {
      if (peer.artist) return {name: 'artist-about', params: {artistId: peer.artist.id}}
      if (peer.profile) return {name: 'fan-profile', params: {id: peer.profile.id}}
      return null
    },
    conversationClass (c) {
      var cls = ['conversation']
      if (this.activePeer && c.peer.id === this.activePeer.id) {
        cls.push('active')
      } else if (c.unread_count > 0) {
        cls.push('unread')
      }
      return cls
    },
    messageClass (msg) {
      return ['message', msg.direction]
    },
    formatDate (date) {
      return moment(date).fromNow()
    },
    linkifyHandles,
    activateConversation (c) {
      if (this.activePeer && this.activePeer.id === c.peer.id) return
      this.activePeer = c.peer
      this.loadMessages()

      if (c.unread_count) {
        c.unread_count = 0
        this.markAsRead()
      }
    },
    async blockConversation (c) {
      await this.$store.dispatch('profile/blockProfile', { id: c.peer.profile.id })
      this.$store.dispatch('flash/showMessage', { message: 'User blocked' })
      this.initComponent()
    },
    msgLines (msg) {
      return msg.text.split(/[\n\r]+/)
    },
    onKeyUp (ev) {
      if (ev.key !== 'Enter') return
      this.send()
    },
    async send () {
      if (!this.activePeer || !this.text.trim()) return
      let newMsg

      try {
        newMsg = await this.$store.dispatch('profile/sendMessage', {
          peerId: this.activePeer.id,
          text: this.text.trim(),
          inboxId: this.inbox.id
        })
      } catch (e) {
        let err = 'Can\'t send message'
        if (e.response && e.response.data && e.response.data.recipient) err = e.response.data.recipient
        this.$store.dispatch('flash/showMessage', { message: err, type: 'error' })
        return
      }

      this.text = ''
      this.messages.push(newMsg)
    },
    doScroll () {
      Vue.nextTick(() => {
        const el = this.$refs.messages
        if (!el) return
        el.scrollTop = el.scrollHeight - el.clientHeight
      })
    },

    ...profileHelpers
  }
}
</script>



// WEBPACK FOOTER //
// src/components/widgets/Messages.vue